import { useRef, useCallback, useState } from 'preact/hooks';
import useCalendarInit from '../hooks/useCalendarInit';
import useCalendarUpdate from '../hooks/useCalendarUpdate';
import { 
  setPickerDate, 
  setDatePickerText as setDatePickerTextHelper,
  addButtonToPicker,
  addContentToPicker,
} from '../utils/pickerHelpers';
import stringToDate from '../../../utils/stringToDate';
import useWidgetConfig from '../../../context/WidgetConfig/useWidgetConfig';
import translations, { DEFAULT_LANG } from '../../../translations/i18n';

/**
 * Calendar Component - Invisible component that manages pickadate instance
 * Handles initialization and updates of the calendar
 * Does not render any visible elements
 * 
 * @param {Object} props
 * @param {Object} props.inputRef - Ref to the input element
 * @param {Object} props.fieldWrapperRef - Ref to the field wrapper
 * @param {Object} props.config - Calendar configuration
 * @param {Object} props.callbacks - Lifecycle callbacks
 * @param {Object} props.eventHandlers - Custom event handlers
 * @param {Object} props.options - Additional options
 * @param {Array} props.events - Calendar events to display on days
 */
const Calendar = ({
  inputRef,
  fieldWrapperRef,
  config,
  callbacks,
  eventHandlers,
  options = {},
  events = [], // Calendar events to display on days
  }) => {
  const {
    dateFormat,
    openTicketText,
    openTicketAdText,
    openTicketDepartureText,
    openTicketReturnText,
    newText,
    onOpenTicketDate,
    way,
  } = config;

  // Local state to track current visible month
  // This triggers badge/emoji updates when the month changes
  const [currentMonth, setCurrentMonth] = useState(null);

  // Get language from WidgetConfig context (same source as IntlProvider)
  const widgetConfig = useWidgetConfig();
  const lang = widgetConfig?.lang || DEFAULT_LANG;
  const dictionary = translations[lang] || translations[DEFAULT_LANG];

  // Create action functions that will be passed to hooks
  // These wrap utility functions and provide a clean API
  
  /**
   * Set the input value to be open ticket and close the picker
   * Specific function for "open ticket" functionality
   */
  const setOpenDate = useCallback(() => {
    const $input = inputRef.current;
    if (!$input) return;

    // Set the input value to be open ticket
    $input.val(openTicketText);
    // Close the picker
    $input.pickadate('picker').close();

    if (typeof onOpenTicketDate === 'function') {
      onOpenTicketDate();
    }
  }, [inputRef, openTicketText, onOpenTicketDate]);

  /**
   * Set a date on the picker
   */
  const setDate = useCallback((value) => {
    setPickerDate({ 
      $input: inputRef.current, 
      value, 
      openTicketText, 
      dateFormat, 
      setOpenDateFn: setOpenDate, 
      stringToDateFn: stringToDate 
    });
  }, [inputRef, openTicketText, dateFormat, setOpenDate]);

  const openButtonInsertedRef = useRef(false);

  /**
   * Add the open ticket button to the calendar
   * Specific function for "open ticket" functionality using generic helpers
   */
  const setOpenTicketBtn = useCallback((fieldName, calendarOpenFlag) => {
    const $input = inputRef.current;
    if (!$input) return;
    if (!calendarOpenFlag || openButtonInsertedRef.current) {
      return;
    }

    const askText = way === 'departure' 
      ? openTicketDepartureText 
      : openTicketReturnText;

    // Add the ticket wrapper HTML after .picker__box using generic helper
    const ticketHtml = `
      <div class="picker-ticket">
        <div class="picker-ticket-wrapper">
          <p><b>${askText}</b></p>
          <p>${openTicketAdText}</p>
          <span class="picker-ticket-new">
            ${newText}!
          </span>
        </div>
      </div>
    `;

    addContentToPicker({
      $input,
      selector: '.picker__box',
      html: ticketHtml,
      method: 'after',
      insertedRef: null, // Don't mark as inserted yet, we still need to add the button
    });

    // Add the button after the wrapper using generic helper
    addButtonToPicker({
      $input,
      buttonConfig: {
        text: openTicketText,
        className: 'picker-ticket-button',
        onClick: () => setOpenDate(),
      },
      position: '.picker-ticket-wrapper',
      insertedRef: openButtonInsertedRef,
    });

    // Mark as inserted after both elements are added
    openButtonInsertedRef.current = true;
  }, [inputRef, way, openTicketText, openTicketDepartureText, openTicketReturnText, openTicketAdText, newText, setOpenDate]);

  /**
   * Add the date picker text to the calendar
   */
  const setDatePickerText = useCallback((fieldName) => {
    setDatePickerTextHelper({ 
      $input: inputRef.current, 
      field: fieldName, 
      departureDatePickerText: config.departureDatePickerText, 
      returnDatePickerText: config.returnDatePickerText, 
      title: config.title 
    });
  }, [inputRef, config]);

  // Create actions object
  const actions = {
    setDate,
    setOpenTicketBtn,
    setDatePickerText,
  };

  // Initialize calendar
  useCalendarInit({
    inputRef,
    fieldWrapperRef,
    config,
    callbacks: {
      ...callbacks,
      onMonthChange: ({ field: changedField, visibleMonth }) => {
        // Update local state to trigger badge/emoji refresh
        if (visibleMonth) {
          setCurrentMonth(visibleMonth);
        }
        // Call parent callback if provided
        if (callbacks.onMonthChange) {
          callbacks.onMonthChange({ field: changedField || config.field, visibleMonth });
        }
      }
    },
    actions,
    eventHandlers,
    options,
    events, // Pass events to hook
  });

  // Handle calendar updates
  useCalendarUpdate({
    inputRef,
    config,
    actions,
    events, // Pass events to update hook
    currentMonth, // Pass currentMonth to trigger badge/emoji updates when month changes
    dictionary, // Pass translation dictionary for event labels
  });

  // This component doesn't render anything
  return null;
};

export default Calendar;

